import React, { useMemo, useState } from "react";
import {
    Box,
    Container,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import SectionTitle from "../components/SectionTitle";
import mockPhotos from "../data/mockPhotos";

const ITEMS_PER_PAGE = 6;

function getGridColumnSpan(size) {
    switch (size) {
        case "large":
            return {
                xs: "span 1",
                sm: "span 2",
                lg: "span 2",
            };
        case "medium":
            return {
                xs: "span 1",
                sm: "span 1",
                lg: "span 1",
            };
        case "small":
        default:
            return {
                xs: "span 1",
                sm: "span 1",
                lg: "span 1",
            };
    }
}

function getAspectRatio(size) {
    switch (size) {
        case "large":
            return {
                xs: "4 / 5",
                sm: "16 / 10",
                lg: "16 / 9",
            };
        case "medium":
            return {
                xs: "4 / 5",
                sm: "1 / 1",
                lg: "5 / 6",
            };
        case "small":
        default:
            return {
                xs: "4 / 5",
                sm: "1 / 1",
                lg: "4 / 5",
            };
    }
}

export default function Fotos() {
    const [page, setPage] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const totalPages = Math.ceil(mockPhotos.length / ITEMS_PER_PAGE);

    const currentItems = useMemo(() => {
        const start = page * ITEMS_PER_PAGE;
        return mockPhotos.slice(start, start + ITEMS_PER_PAGE);
    }, [page]);

    const handleChangePage = (direction) => {
        if (isAnimating) return;

        setIsAnimating(true);

        setTimeout(() => {
            setPage((prev) => {
                if (direction === "next") {
                    return prev === totalPages - 1 ? 0 : prev + 1;
                }
                return prev === 0 ? totalPages - 1 : prev - 1;
            });

            setTimeout(() => {
                setIsAnimating(false);
            }, 80);
        }, 260);
    };

    return (
        <Box
            id="fotos"
            sx={{
                position: "relative",
                py: { xs: 8, sm: 9, md: 10, lg: 11 },
                overflow: "hidden",
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    px: { xs: 2, md: 3 },
                    maxWidth: 1600,
                    mx: "auto",
                }}
            >
                {/* HEADER */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", lg: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", lg: "row" },
                        gap: { xs: 3, md: 4 },
                        mb: { xs: 4, md: 5 },
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", md: 980 },
                        }}
                    >
                        <SectionTitle
                            eyebrow="portfólio"
                            title="Frames que permanecem"
                            subtitle="Uma curadoria visual de eventos, experiências e atmosferas capturadas com intenção, direção e percepção estética."
                        />
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            flexShrink: 0,
                            alignSelf: { xs: "flex-start", lg: "center" },
                        }}
                    >
                        <IconButton
                            onClick={() => handleChangePage("prev")}
                            disabled={isAnimating}
                            sx={{
                                width: { xs: 48, md: 54 },
                                height: { xs: 48, md: 54 },
                                border: "1px solid rgba(255,255,255,0.10)",
                                bgcolor: "rgba(255,255,255,0.03)",
                                color: "var(--verdeClaro)",
                                backdropFilter: "blur(10px)",
                                transition: "all 300ms cubic-bezier(0.22, 1, 0.36, 1)",
                                "&:hover": {
                                    bgcolor: "rgba(172,201,14,0.08)",
                                    borderColor: "rgba(172,201,14,0.28)",
                                    transform: "translateY(-2px)",
                                },
                                "&.Mui-disabled": {
                                    opacity: 0.45,
                                    color: "rgba(255,255,255,0.35)",
                                },
                            }}
                        >
                            <ChevronLeft sx={{ fontSize: { xs: 26, md: 30 } }} />
                        </IconButton>

                        <IconButton
                            onClick={() => handleChangePage("next")}
                            disabled={isAnimating}
                            sx={{
                                width: { xs: 48, md: 54 },
                                height: { xs: 48, md: 54 },
                                border: "1px solid rgba(255,255,255,0.10)",
                                bgcolor: "rgba(255,255,255,0.03)",
                                color: "var(--verdeClaro)",
                                backdropFilter: "blur(10px)",
                                transition: "all 300ms cubic-bezier(0.22, 1, 0.36, 1)",
                                "&:hover": {
                                    bgcolor: "rgba(172,201,14,0.08)",
                                    borderColor: "rgba(172,201,14,0.28)",
                                    transform: "translateY(-2px)",
                                },
                                "&.Mui-disabled": {
                                    opacity: 0.45,
                                    color: "rgba(255,255,255,0.35)",
                                },
                            }}
                        >
                            <ChevronRight sx={{ fontSize: { xs: 26, md: 30 } }} />
                        </IconButton>
                    </Stack>
                </Box>

                {/* GRID */}
                <Box
                    sx={{
                        opacity: isAnimating ? 0 : 1,
                        transform: isAnimating
                            ? "translateY(18px) scale(0.992)"
                            : "translateY(0px) scale(1)",
                        transition:
                            "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                        willChange: "opacity, transform",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: { xs: 2, md: 2.5 },
                            alignItems: "stretch",
                        }}
                    >
                        {currentItems.map((photo) => (
                            <Box
                                key={photo.id}
                                sx={{
                                    gridColumn: getGridColumnSpan(photo.size),
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        aspectRatio: getAspectRatio(photo.size),
                                        borderRadius: { xs: "22px", md: "28px" },
                                        overflow: "hidden",
                                        backgroundColor: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
                                        isolation: "isolate",
                                        transform: "translateZ(0)",
                                        transition:
                                            "transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease",
                                        "&:hover": {
                                            transform: "translateY(-6px)",
                                            borderColor: "rgba(172,201,14,0.22)",
                                            boxShadow: "0 30px 100px rgba(0,0,0,0.34)",
                                        },
                                        "&:hover .foto-image": {
                                            transform: "scale(1.05)",
                                        },
                                        "&:hover .foto-overlay-soft": {
                                            opacity: 0.72,
                                        },
                                        "&:hover .foto-line": {
                                            width: 72,
                                        },
                                    }}
                                >
                                    <Box
                                        className="foto-image"
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            backgroundImage: `url(${photo.image})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            transition:
                                                "transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
                                        }}
                                    />

                                    <Box
                                        className="foto-overlay-soft"
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            opacity: 0.58,
                                            transition: "opacity 500ms ease",
                                            background:
                                                "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.22) 40%, rgba(0,0,0,0.88) 100%)",
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background:
                                                "linear-gradient(135deg, rgba(172,201,14,0.06) 0%, rgba(172,201,14,0) 36%)",
                                            zIndex: 1,
                                            pointerEvents: "none",
                                        }}
                                    />

                                    <Stack
                                        justifyContent="flex-end"
                                        sx={{
                                            position: "relative",
                                            zIndex: 2,
                                            height: "100%",
                                            p: { xs: 2.2, sm: 2.4, md: 3 },
                                            pb: { xs: 2.8, md: 4 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                mb: 1.3,
                                                display: "inline-flex",
                                                alignSelf: "flex-start",
                                                maxWidth: "100%",
                                                px: 1.4,
                                                py: 0.7,
                                                borderRadius: 999,
                                                border: "1px solid rgba(255,255,255,0.14)",
                                                bgcolor: "rgba(0,0,0,0.22)",
                                                backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "rgba(255,255,255,0.86)",
                                                    fontSize: 11,
                                                    fontWeight: 800,
                                                    letterSpacing: "0.14em",
                                                    textTransform: "uppercase",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {photo.category}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            sx={{
                                                color: "var(--branco)",
                                                fontWeight: 900,
                                                lineHeight: 1.02,
                                                letterSpacing: "-0.03em",
                                                fontSize: {
                                                    xs: "1.15rem",
                                                    sm: photo.size === "large" ? "1.45rem" : "1.18rem",
                                                    lg: photo.size === "large" ? "1.7rem" : "1.22rem",
                                                },
                                                maxWidth: { xs: "100%", md: 560 },
                                                pr: { xs: 0.5, md: 1 },
                                            }}
                                        >
                                            {photo.title}
                                        </Typography>

                                        <Box
                                            className="foto-line"
                                            sx={{
                                                width: 44,
                                                height: "2px",
                                                bgcolor: "var(--verdeClaro)",
                                                mt: 1.4,
                                                transition: "width 280ms ease",
                                            }}
                                        />
                                    </Stack>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* PAGINAÇÃO */}
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    sx={{ mt: { xs: 3, md: 3.5 } }}
                >
                    {Array.from({ length: totalPages }).map((_, idx) => (
                        <Box
                            key={idx}
                            onClick={() => !isAnimating && setPage(idx)}
                            sx={{
                                width: page === idx ? 30 : 8,
                                height: 8,
                                borderRadius: 999,
                                bgcolor:
                                    page === idx
                                        ? "var(--verdeClaro)"
                                        : "rgba(255,255,255,0.18)",
                                transition: "all 400ms cubic-bezier(0.22, 1, 0.36, 1)",
                                cursor: "pointer",
                            }}
                        />
                    ))}
                </Stack>
            </Container>
        </Box>
    );
}